import React from "react";
import { Box } from "@mui/material";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import { useTheme as useCustomTheme } from "../../../theme/useTheme";

/**
 * Tab bar without background, over the page grey (Stripe-style): semibold labels,
 * the active one in the primary colour with a 2px underline the width of its text,
 * and a white rounded block on hover. `tab.count` adds a small grey badge.
 *
 * The shared components/Tabs.jsx is a numbered wizard pill bar, which does not
 * fit a read-only screen, so this keeps the lighter look.
 */
export function UnderlineTabs({ tabs, value, onChange, size = "md" }) {
  const theme = useMuiTheme();
  const { neutrals } = useCustomTheme();
  const fontSize = size === "sm" ? "0.8125rem" : "0.875rem";

  return (
    <Box
      role="tablist"
      sx={{
        display: "flex",
        gap: 0.5,
        borderBottom: `1px solid ${theme.palette.divider}`,
        overflowX: "auto",
        overflowY: "hidden",
        flexShrink: 0,
      }}
    >
      {tabs.map((tab) => {
        const isActive = tab.value === value;
        return (
          <Box
            key={tab.value}
            component="button"
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(tab.value)}
            sx={{
              position: "relative",
              background: "transparent",
              border: "none",
              cursor: "pointer",
              padding: "0 0 8px",
              fontFamily: theme.typography.fontFamily,
              fontSize,
              fontWeight: 600,
              lineHeight: "20px",
              whiteSpace: "nowrap",
              color: isActive ? theme.palette.primary.main : theme.palette.text.secondary,
              "&::after": {
                content: '""',
                position: "absolute",
                left: 8,
                right: 8,
                bottom: 0,
                height: 2,
                borderRadius: 2,
                backgroundColor: isActive ? theme.palette.primary.main : "transparent",
              },
              "&:hover": {
                color: isActive ? theme.palette.primary.main : theme.palette.text.primary,
              },
              "&:hover > span": { backgroundColor: neutrals.white },
            }}
          >
            <Box
              component="span"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                padding: "4px 8px",
                borderRadius: "6px",
                transition: "background-color 0.15s ease",
              }}
            >
              {tab.label}
              {tab.count !== undefined && (
                <Box
                  component="span"
                  sx={{
                    px: 0.75,
                    borderRadius: "6px",
                    backgroundColor: neutrals.gray[200],
                    color: theme.palette.text.secondary,
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    lineHeight: "18px",
                  }}
                >
                  {tab.count}
                </Box>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}

export default UnderlineTabs;
